import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { StyledEngineProvider } from "@mui/material/styles";
import ktmFontStylesUrl from "@src/assets/fonts/blenderPro/index.css";
import hqvFontStylesUrl from "@src/assets/fonts/helveticaNeue/index.css";
import ggFontStylesUrl from "@src/assets/fonts/industry/index.css";
import cfmFontStylesUrl from "@src/assets/fonts/raleway/index.css";
import wpFontStylesUrl from "@src/assets/fonts/robotoCondensed/index.css";
import mvaFontStylesUrl from "@src/assets/fonts/sharpGrotesk/index.css";
import defaultFontStylesUrl from "@src/assets/fonts/sourceSansPro/index.css";
import { ConfigTheme, type InputAttributeConfig } from "@src/context/attributeConfig/AttributeConfigContext";
import { createRoot, type Root } from "react-dom/client";
import swiperStylesUrl from "swiper/swiper-bundle.css";

import { App } from "./App";

let root: Root;
let styleElements: HTMLStyleElement[] = [];

// Helper to fetch and inject CSS into shadow DOM
const injectStylesheet = async (shadowRoot: ShadowRoot, cssUrl: string): Promise<HTMLStyleElement> => {
    const response = await fetch(cssUrl);
    const cssText = await response.text();
    const style = document.createElement("style");
    style.textContent = cssText;
    shadowRoot.appendChild(style);

    return style;
};

// Helper to inject styles from URL (for fonts that need to be in main document)
const injectStylesheetIntoDocument = (cssUrl: string): HTMLLinkElement => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = cssUrl;
    document.head.appendChild(link);

    return link;
};

const loadStyles = async (shadowRoot: ShadowRoot) => {
    // Inject Swiper styles into shadow DOM
    const swiperStyle = await injectStylesheet(shadowRoot, swiperStylesUrl);
    styleElements.push(swiperStyle);
};

const getFontUrlForTheme = (theme: ConfigTheme): string => {
    switch (theme) {
        case ConfigTheme.KTM:
        case ConfigTheme.SPORTCAR:
            return ktmFontStylesUrl;
        case ConfigTheme.Husqvarna:
            return hqvFontStylesUrl;
        case ConfigTheme.GasGas:
            return ggFontStylesUrl;
        case ConfigTheme.MVAgusta:
            return mvaFontStylesUrl;
        case ConfigTheme.CFMOTO:
            return cfmFontStylesUrl;
        case ConfigTheme.WPPerformanceSport:
            return wpFontStylesUrl;
        default:
            return defaultFontStylesUrl;
    }
};

export const render = async (shadowRoot: ShadowRoot, shadowRootElement: HTMLElement, attributeConfig: InputAttributeConfig) => {
    const currentFontUrl = getFontUrlForTheme(attributeConfig.theme ?? ConfigTheme.Default);

    // Create emotion cache for MUI styles in shadow DOM
    const emotionCache = createCache({
        key: "pmag-spf",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        container: shadowRoot as any,
        prepend: true,
    });

    if (!root) {
        root = createRoot(shadowRootElement);
        await loadStyles(shadowRoot);
        // Inject font styles into main document (fonts don't work in shadow DOM)
        injectStylesheetIntoDocument(currentFontUrl);
    } else {
        // Clean up old styles
        styleElements.forEach((style) => style.remove());
        styleElements = [];

        root.unmount();
        root = createRoot(shadowRootElement);
        await loadStyles(shadowRoot);
        injectStylesheetIntoDocument(currentFontUrl);
    }

    root.render(
        <CacheProvider value={emotionCache}>
            <StyledEngineProvider injectFirst>
                <App shadowRoot={shadowRoot} shadowRootElement={shadowRootElement} inputAttributeConfig={attributeConfig} />
            </StyledEngineProvider>
        </CacheProvider>,
    );
};
